import {AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {MasterDataService} from '../services/master-data.service';
import {AuthService} from '../../../auth/auth.service';
import {Router} from '@angular/router';
import {FormControl} from '@angular/forms';
import {take, takeUntil} from 'rxjs/operators';
import {MatSelect} from '@angular/material/select';
import {Subject} from 'rxjs/Subject';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {MasterCustomerService} from '../services/master-customer.service';
import {IMasterCustomer} from '../interfaces/IMasterCustomer';
import {ToastrService} from 'ngx-toastr';
import {WorkspaceService} from '../services/workspace.service';

@Component({
    selector: 'app-workspace-select',
    templateUrl: './workspace-select.component.html',
    styleUrls: ['./workspace-select.component.css'],
    providers: [MasterDataService, AuthService]
})
export class WorkspaceSelectComponent implements OnInit, AfterViewInit, OnDestroy {
    private loading = false;

    public masterCustomerControl: FormControl = new FormControl();
    public masterCustomerFilterCtrl: FormControl = new FormControl();

    public filteredMasterCustomers: ReplaySubject<IMasterCustomer[]> = new ReplaySubject<IMasterCustomer[]>(1);

    private masterCustomers = new Array<IMasterCustomer>();
    @Output() notify: EventEmitter<IMasterCustomer> = new EventEmitter<IMasterCustomer>();
    @ViewChild('singleSelect') singleSelect: MatSelect;
    private lastFetched: Date;

    protected _onDestroy = new Subject<void>();

    constructor(
        private masterCustomerService: MasterCustomerService,
        private authService: AuthService,
        private router: Router,
        private toast: ToastrService,
        private workspaceService: WorkspaceService
    ) { }

    ngOnInit() {
        this.lastFetched = new Date();
        this.getData();

        this.masterCustomerFilterCtrl.valueChanges
            .pipe(takeUntil(this._onDestroy))
            .subscribe(() => {
                this.filterMasterCustomers();
            });
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    ngAfterViewInit() {
        this.setInitialValue();
    }

    protected setInitialValue() {
        this.masterCustomerControl.patchValue(this.authService.workspaceMasterCustomerID);
        this.filteredMasterCustomers
            .pipe(take(1), takeUntil(this._onDestroy))
            .subscribe(() => {
                // setting the compareWith property to a comparison function
                // triggers initializing the selection according to the initial value of
                // the form control (i.e. _initializeSelection())
                // this needs to be done after the filteredBanks are loaded initially
                // and after the mat-option elements are available
                if (this.singleSelect !== undefined) {
                    this.singleSelect.compareWith = (a: IMasterCustomer, b: IMasterCustomer) => a && b && a.CU0_ID === b.CU0_ID;
                }
            });
    }

    fetchFreshData() {
        const gap = (new Date()).getTime() - this.lastFetched.getTime();
        if (gap > 600000 || (gap > 60000 && this.masterCustomers.length === 0)) { // 10 minutes OR 1 minute with no data in it
            this.getData();
        }
    }

    getData() {
        this.loading = true;
        this.masterCustomerService.getDataListForSelection({}).subscribe(result => {
            if (result.hasOwnProperty('data')) {
                this.lastFetched = new Date();
                this.masterCustomers = result.data;
                this.filterMasterCustomers();
                const matchingIndex = this.masterCustomers.findIndex(
                    masterCustomer => Number(masterCustomer.CU0_ID) === Number(this.authService.workspaceMasterCustomerID)
                );
                if (this.masterCustomers.length < 1 ||  matchingIndex < 0) {
                    this.onChangeMasterCustomer(null, false);
                } else {
                    this.workspaceService.setWorkspaceMasterCustomer({
                        CU0_ID: this.masterCustomers[matchingIndex].CU0_ID,
                        CU0_ALLOW_INVENTORY_MANAGEMENT: this.masterCustomers[matchingIndex].CU0_ALLOW_INVENTORY_MANAGEMENT
                    });
                    this.workspaceService.next({});
                }
            }
            this.loading = false;
        }, () => {
            this.loading = false;
        });
    }

    onChangeMasterCustomer(CU0_ID, navigateToMasterData = true) {
        this.authService.setWorkspaceMasterCustomerID(CU0_ID);
        this.masterCustomerService.setSelectedMasterCustomer(CU0_ID).subscribe(result => {
            if (result.success) {
                this.workspaceService.setWorkspaceMasterCustomer(result.masterCustomer);
                this.workspaceService.next({});
                if (navigateToMasterData) {
                    this.router.navigate(['/dashboard'], {skipLocationChange: false}).then(() => {
                        this.router.navigate(['/order-entry-system/master-data']);
                    });
                }
            } else {
                this.toast.error('Failed to save Workspace preference.', 'Service Error');
                this.workspaceService.setWorkspaceMasterCustomer({CU0_ID: 0, CU0_ALLOW_INVENTORY_MANAGEMENT: 0});
                this.workspaceService.next({});
            }
        }, () => {
            this.toast.error('Unable to save Workspace preference.', 'Service Error');
        });
    }

    protected filterMasterCustomers() {
        if (!this.masterCustomers) {
            return;
        }
        // get the search keyword
        let search = this.masterCustomerFilterCtrl.value;
        if (!search) {
            this.filteredMasterCustomers.next(this.masterCustomers.slice());
            return;
        } else {
            search = search.toLowerCase();
        }
        // filter the companies
        this.filteredMasterCustomers.next(
            this.masterCustomers.filter(company => company.CU0_NAME.toLowerCase().indexOf(search) > -1)
        );
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/oes/select-workspace/workspace-select.component.ts